import { useState, useEffect } from 'react'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { ConfigProvider, App as AntApp, Layout, Button, Dropdown, Modal, Typography, Descriptions, Tag, Grid, Tooltip } from 'antd'
import {
  UserOutlined,
  LogoutOutlined,
  LoadingOutlined,
  UploadOutlined,
  SettingOutlined,
  TeamOutlined,
  BugOutlined,
  ApiOutlined,
  ToolOutlined,
  SwapOutlined,
  ControlOutlined,
  ClusterOutlined,
  EditOutlined,
  AuditOutlined,
  MessageOutlined,
  IdcardOutlined,
  CreditCardOutlined,
  UserAddOutlined,
  MenuOutlined,
  DatabaseOutlined
} from '@ant-design/icons'
import theme from './theme/antd-theme'
import { useAuth } from './hooks/useAuth'
// CompactOrganizationSwitcher removed - org switching now in user dropdown
import { WorkflowNav } from './components/WorkflowNav'
import { stackService } from './services/stackService'
import errorService from './services/errorService'
import { featureTierService } from './services/featureTierService'
import { IdleTimeoutWarning } from './components'
import { FeedbackModal } from './components/feedback'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { EulaModal, needsEulaAcceptance } from './components/auth/EulaModal'
import { MetadataInspector } from './components/dev/MetadataInspector'
import { TimezoneProvider } from './contexts/TimezoneContext'
import type { ErrorContext } from './types/feedback.types'
import logo from './assets/Logo.png'
// Landing page
import Home from './pages/Home'
// Auth pages
import LoginPage from './pages/auth/LoginPage'
import LogoutPage from './pages/auth/LogoutPage'
import OAuthCallbackPage from './pages/auth/OAuthCallbackPage'
// Module pages (main app sections)
import DataPage from './pages/modules/DataPage'
import CostsPage from './pages/modules/CostsPage'
import PricesPage from './pages/modules/PricesPage'
import ExportsPage from './pages/modules/ExportsPage'
// Workflows
import SmbSyncPage from './pages/workflows/smb-sync/SmbSyncPage'
// Tools pages
import ConnectionsPage from './pages/tools/ConnectionsPage'
import FieldConfigurationDemo from './pages/tools/FieldConfigurationDemo'
// Admin pages (RP staff)
import OrgSwitchPage from './pages/admin/OrgSwitchPage'
import AuditLogsPage from './pages/admin/AuditLogsPage'
import FeedbackManagementPage from './pages/admin/FeedbackManagementPage'
import ProvisionUserPage from './pages/admin/ProvisionUserPage'
// Dev pages
import ErrorTestPage from './pages/dev/ErrorTestPage'
// User pages
import Profile from './pages/user/Profile'
import MyFeedbackPage from './pages/user/feedback'
import BillingPage from './pages/user/billing'
import './index.css'

const { Header, Content } = Layout

function App() {
  const location = useLocation()
  const navigate = useNavigate()

  // State machine-based auth hook - single source of truth for auth state
  const {
    state: authState,
    user,
    isAuthenticated,
    logout,
    switchOrg
  } = useAuth()

  const [tokenInfoModalOpen, setTokenInfoModalOpen] = useState(false)
  const [metadataInspectorOpen, setMetadataInspectorOpen] = useState(false)

  // Responsive breakpoint detection for mobile hamburger menu
  const screens = Grid.useBreakpoint()
  const showFullUtilities = screens.sm !== false  // 576px+ shows full utilities

  // Check if we're on auth pages that should render without the header/layout
  const isAuthPage = ['/login', '/logout', '/oauth/callback'].includes(location.pathname)

  // Determine if we should show loading screen (initializing or authenticating)
  const isLoading = authState === 'initializing' || authState === 'authenticating'

  // Debug current auth state
  console.log('🔄 App render - Auth state:', authState, { isAuthenticated, hasUser: !!user, userEmail: user?.email })

  // DEBUG: Expose user to window for console inspection
  useEffect(() => {
    if (user) {
      (window as any).currentUser = user
      console.log('👤 User data available in console as window.currentUser:', user)
    }
  }, [user])

  // Update browser tab title based on current organization
  useEffect(() => {
    const orgName = user?.currentOrganization?.name
    document.title = orgName
      ? `Rocket Pricing | ${orgName}`
      : 'Rocket Pricing | Capture Your Value'
  }, [user?.currentOrganization?.name])

  // Register error service feedback callback
  useEffect(() => {
    errorService.setFeedbackCallback((context) => {
      setErrorContext(context)
      setFeedbackModalOpen(true)
    })
  }, [])

  // Dropdown button state for ARIA
  const [toolsOpen, setToolsOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [devOpen, setDevOpen] = useState(false)
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [errorContext, setErrorContext] = useState<ErrorContext | undefined>(undefined)

  // Utility button styles now handled by .header-btn class in index.css
  // "Borderless at rest" pattern - unified with module nav buttons

  // Keyboard handler for dropdown buttons
  const handleDropdownKeyDown = (e: React.KeyboardEvent, onClick: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  // Tools dropdown items (right side, with utilities) - tier-gated
  const toolsMenuItems = [
    {
      key: 'smb-sync',
      label: 'SMB Vendor Import',
      icon: <UploadOutlined />,
      tier: 'GA' as const,
      onClick: () => navigate('/prototypes/smb-sync')
    },
    {
      key: 'my-feedback',
      label: 'My Feedback',
      icon: <MessageOutlined />,
      tier: 'GA' as const,
      onClick: () => navigate('/feedback')
    }
  ].filter(item => featureTierService.hasFeatureAccess(user, item.tier))

  // Settings dropdown items (right side, owner/admin only) - tier-gated
  const settingsMenuItems = [
    {
      key: 'connections',
      label: 'Connections',
      icon: <ApiOutlined />,
      tier: 'Dev' as const,
      onClick: () => navigate('/connections')
    },
    {
      key: 'field-config',
      label: 'Field Configuration',
      icon: <ControlOutlined />,
      tier: 'Dev' as const,
      onClick: () => navigate('/field-config-demo')
    }
  ].filter(item => featureTierService.hasFeatureAccess(user, item.tier))

  // Dev dropdown items (right side, dev env only)
  const devMenuItems = [
    {
      key: 'stack-switcher',
      label: `Stack: ${stackService.getSelectedStack()}`,
      icon: <ClusterOutlined />,
      children: stackService.getAllStacks().map(stack => ({
        key: `stack-${stack.name}`,
        label: stack.displayName,
        onClick: () => {
          stackService.setSelectedStack(stack.name)
          window.location.reload()
        }
      }))
    },
    // RP Admin org switch - only for rp_admin/rp_support
    ...(featureTierService.hasInternalAccess(user) ? [
      {
        key: 'org-switch',
        label: 'Switch Org (Admin)',
        icon: <SwapOutlined />,
        onClick: () => navigate('/rp-admin/org-switch')
      },
      {
        key: 'provision-user',
        label: 'Provision User',
        icon: <UserAddOutlined />,
        onClick: () => navigate('/admin/provision-user')
      },
      {
        key: 'audit-logs',
        label: 'Audit Logs',
        icon: <AuditOutlined />,
        onClick: () => navigate('/admin/audit-logs')
      },
      {
        key: 'feedback-management',
        label: 'Feedback Management',
        icon: <MessageOutlined />,
        onClick: () => navigate('/rp-admin/feedback')
      }
    ] : []),
    {
      key: 'token-info',
      label: 'Token Info',
      icon: <IdcardOutlined />,
      onClick: () => setTokenInfoModalOpen(true)
    },
    {
      key: 'metadata-inspector',
      label: 'Metadata Inspector',
      icon: <DatabaseOutlined />,
      onClick: () => setMetadataInspectorOpen(true)
    },
    { type: 'divider' as const },
    {
      key: 'error-test',
      label: 'Error Test',
      icon: <BugOutlined />,
      onClick: () => navigate('/error-test')
    }
  ]

  // Mobile hamburger menu - combines all menus into one
  const mobileMenuItems = [
    ...(toolsMenuItems.length > 0 ? [
      { key: 'tools-header', label: 'Tools', type: 'group' as const },
      ...toolsMenuItems,
      { type: 'divider' as const }
    ] : []),
    ...(settingsMenuItems.length > 0 ? [
      { key: 'settings-header', label: 'Settings', type: 'group' as const },
      ...settingsMenuItems,
      { type: 'divider' as const }
    ] : []),
    { key: 'account-header', label: 'Account', type: 'group' as const },
    {
      key: 'user-name',
      label: user?.name || user?.email || 'Account',
      disabled: true
    },
    {
      key: 'profile',
      label: 'Edit Profile',
      icon: <EditOutlined />,
      onClick: () => navigate('/profile')
    },
    ...(user?.organizations && user.organizations.length > 1 ? [{
      key: 'org-switcher',
      label: user.currentOrganization?.name || 'Switch Org',
      icon: <TeamOutlined />,
      children: user.organizations.map(org => ({
        key: `org-${org.id}`,
        label: org.name,
        onClick: () => switchOrg(org.id)
      }))
    }] : []),
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => navigate('/logout')
    },
    ...(featureTierService.hasInternalAccess(user) ? [
      { type: 'divider' as const },
      { key: 'dev-header', label: 'Developer', type: 'group' as const },
      ...devMenuItems
    ] : [])
  ]

  // Show loading screen during auth initialization to prevent login page flash
  // This happens on initial page load and during page transitions while checking session
  if (isLoading && !isAuthPage) {
    return (
      <ConfigProvider theme={theme}>
        <AntApp>
          <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--rp-gradient-background)'
          }}>
            <LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />
          </div>
        </AntApp>
      </ConfigProvider>
    )
  }

  // Render auth pages without the header/layout wrapper:
  // 1. Explicit auth pages (/login, /logout, /oauth/callback)
  // 2. ANY page when user is definitively unauthenticated (not just loading)
  const shouldShowAuthPage = isAuthPage || authState === 'unauthenticated'

  if (shouldShowAuthPage) {
    return (
      <ConfigProvider theme={theme}>
        <AntApp>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
            {/* Any other URL when not authenticated shows login */}
            <Route path="*" element={<LoginPage />} />
          </Routes>
        </AntApp>
      </ConfigProvider>
    )
  }

  return (
    <ConfigProvider theme={theme}>
      <AntApp>
        <TimezoneProvider orgTimezone={user?.currentOrganization?.timezone}>
        <Layout style={{ minHeight: '100vh' }}>
        <Header style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible', // Prevent clipping of workflow nav elements
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' // Inara: header shadow for polish
          // Note: background gradient, padding, and height now controlled by theme (antd-theme.ts)
        }}>
          {/* Constrained container matching marketing site max-w-7xl (1280px) */}
          <div style={{
            maxWidth: '1280px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '0 16px'
          }}>
            {/* Left group: Logo + WorkflowNav (left-justified) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden'
            }}>
              <Tooltip
                title={
                  <div>
                    <strong>Home</strong>
                    <br />
                    <span style={{ opacity: 0.8 }}>Launch and monitor workflows</span>
                  </div>
                }
                placement="bottom"
              >
                <img
                  src={logo}
                  alt="Rocket Pricing - Home"
                  role="button"
                  tabIndex={0}
                  className="header-logo"
                  onClick={() => navigate('/')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate('/')
                    }
                  }}
                />
              </Tooltip>
              {isAuthenticated && user && <WorkflowNav user={user} />}
            </div>

            {/* Right group: Utility icons - NEVER shrink */}
            <div
              className="header-utility-group"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0
              }}
            >
            {isAuthenticated && user && (
              showFullUtilities ? (
                <>
                  {/* Full utility dropdowns - Desktop/Tablet (576px+) */}
                  {toolsMenuItems.length > 0 && (
                    <Dropdown
                      menu={{ items: toolsMenuItems }}
                      trigger={['click']}
                      open={toolsOpen}
                      onOpenChange={setToolsOpen}
                    >
                      <div
                        role="button"
                        aria-label="Tools menu"
                        aria-expanded={toolsOpen}
                        aria-haspopup="menu"
                        tabIndex={0}
                        className="header-btn"
                        onKeyDown={(e) => handleDropdownKeyDown(e, () => setToolsOpen(!toolsOpen))}
                      >
                        <ToolOutlined />
                      </div>
                    </Dropdown>
                  )}

                  {featureTierService.hasFeatureAccess(user, 'Dev') && (
                    <Dropdown
                      menu={{ items: settingsMenuItems }}
                      trigger={['click']}
                      open={settingsOpen}
                      onOpenChange={setSettingsOpen}
                    >
                      <div
                        role="button"
                        aria-label="Settings menu"
                        aria-expanded={settingsOpen}
                        aria-haspopup="menu"
                        tabIndex={0}
                        className="header-btn"
                        onKeyDown={(e) => handleDropdownKeyDown(e, () => setSettingsOpen(!settingsOpen))}
                      >
                        <SettingOutlined />
                      </div>
                    </Dropdown>
                  )}

                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'user-name',
                          label: (
                            <div style={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.85)' }}>
                              {user.name || user.email}
                            </div>
                          ),
                          disabled: true,
                          style: { cursor: 'default' }
                        },
                        { type: 'divider' },
                        ...(featureTierService.hasFeatureAccess(user, 'Dev') ? [{
                          key: 'billing',
                          label: 'Plan & Billing',
                          icon: <CreditCardOutlined />,
                          onClick: () => navigate('/billing')
                        }] : []),
                        {
                          key: 'profile',
                          label: 'Edit Profile',
                          icon: <EditOutlined />,
                          onClick: () => navigate('/profile', { state: { from: location.pathname } })
                        },
                        { type: 'divider' },
                        ...(user.organizations && user.organizations.length > 1
                          ? [{
                              key: 'org-switcher',
                              label: user.currentOrganization?.name || 'Unknown Org',
                              icon: <TeamOutlined />,
                              children: [...user.organizations]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(org => ({
                                  key: `org-${org.id}`,
                                  label: org.name,
                                  onClick: () => switchOrg(org.id)
                                }))
                            }]
                          : [{
                              key: 'org-display',
                              label: user.currentOrganization?.name || 'Unknown Org',
                              icon: <TeamOutlined />
                            }]
                        ),
                        { type: 'divider' },
                        {
                          key: 'logout',
                          label: 'Logout',
                          icon: <LogoutOutlined />,
                          onClick: () => navigate('/logout')
                        }
                      ],
                      triggerSubMenuAction: 'click'
                    }}
                    trigger={['click']}
                    open={accountOpen}
                    onOpenChange={setAccountOpen}
                  >
                    <div
                      role="button"
                      aria-label="Account menu"
                      aria-expanded={accountOpen}
                      aria-haspopup="menu"
                      tabIndex={0}
                      className="header-btn"
                      onKeyDown={(e) => handleDropdownKeyDown(e, () => setAccountOpen(!accountOpen))}
                    >
                      {isLoading ? (
                        <LoadingOutlined spin />
                      ) : (
                        <UserOutlined />
                      )}
                    </div>
                  </Dropdown>

                  {featureTierService.hasInternalAccess(user) && (
                    <Dropdown
                      menu={{ items: devMenuItems }}
                      trigger={['click']}
                      open={devOpen}
                      onOpenChange={setDevOpen}
                    >
                      <div
                        role="button"
                        aria-label="Developer tools menu"
                        aria-expanded={devOpen}
                        aria-haspopup="menu"
                        tabIndex={0}
                        className="header-btn"
                        onKeyDown={(e) => handleDropdownKeyDown(e, () => setDevOpen(!devOpen))}
                      >
                        <BugOutlined />
                      </div>
                    </Dropdown>
                  )}
                </>
              ) : (
                /* Mobile hamburger menu - single button for all utilities (<576px) */
                <Dropdown
                  menu={{ items: mobileMenuItems }}
                  trigger={['click']}
                  open={accountOpen}
                  onOpenChange={setAccountOpen}
                >
                  <div
                    role="button"
                    aria-label="Menu"
                    aria-expanded={accountOpen}
                    aria-haspopup="menu"
                    tabIndex={0}
                    className="header-btn"
                    onKeyDown={(e) => handleDropdownKeyDown(e, () => setAccountOpen(!accountOpen))}
                  >
                    <MenuOutlined />
                  </div>
                </Dropdown>
              )
            )}
            {/* Debug logout for when Cognito session exists but app doesn't recognize it */}
            {!isAuthenticated && localStorage.getItem('cognito-auth-tokens') && (
              <Button
                type="text"
                size="small"
                icon={<LogoutOutlined />}
                onClick={() => logout()}
                style={{ color: '#ff4d4f', fontSize: '12px' }}
                title="Clear Cognito session"
              >
                Debug Logout
              </Button>
            )}
            </div>
          </div>
        </Header>

        <Routes>
          {/* Public routes - accessible without authentication */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

          {/* Protected routes - require authentication */}
          {isAuthenticated && user ? (
            <>
              <Route path="/" element={
                <>
                  <IdleTimeoutWarning />
                  <Content style={{
                    padding: '24px',
                    background: 'var(--rp-surface-page)',
                    minHeight: 'calc(100vh - 64px)'
                  }}>
                    <Home user={user} />
                  </Content>
                </>
              } />
              <Route path="/profile" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <Profile />
                </Content>
              } />
              <Route path="/billing" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <BillingPage />
                </Content>
              } />
              {/* Workflow pages */}
              <Route path="/data" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <DataPage />
                </Content>
              } />
              <Route path="/costs" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <CostsPage />
                </Content>
              } />
              <Route path="/prices" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <PricesPage />
                </Content>
              } />
              <Route path="/exports" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <ExportsPage />
                </Content>
              } />
              {/* Utility pages */}
              <Route path="/connections" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <ConnectionsPage />
                </Content>
              } />
              <Route path="/error-test" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <ErrorTestPage />
                </Content>
              } />
              <Route path="/field-config-demo" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <FieldConfigurationDemo />
                </Content>
              } />
              <Route path="/prototypes/smb-sync" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <SmbSyncPage />
                </Content>
              } />
              {/* RP Admin pages - protected routes requiring internal access */}
              <Route path="/rp-admin/org-switch" element={
                <ProtectedRoute requireInternal>
                  <Content style={{
                    padding: '24px',
                    background: 'var(--rp-surface-page)',
                    minHeight: 'calc(100vh - 64px)'
                  }}>
                    <OrgSwitchPage />
                  </Content>
                </ProtectedRoute>
              } />
              <Route path="/admin/audit-logs" element={
                <ProtectedRoute requireInternal>
                  <Content style={{
                    padding: '24px',
                    background: 'var(--rp-surface-page)',
                    minHeight: 'calc(100vh - 64px)'
                  }}>
                    <AuditLogsPage />
                  </Content>
                </ProtectedRoute>
              } />
              <Route path="/admin/provision-user" element={
                <ProtectedRoute requireInternal>
                  <Content style={{
                    padding: '24px',
                    background: 'var(--rp-surface-page)',
                    minHeight: 'calc(100vh - 64px)'
                  }}>
                    <ProvisionUserPage />
                  </Content>
                </ProtectedRoute>
              } />
              <Route path="/rp-admin/feedback" element={
                <ProtectedRoute requireInternal fallbackPath="/feedback" preserveQuery>
                  <Content style={{
                    padding: '24px',
                    background: 'var(--rp-surface-page)',
                    minHeight: 'calc(100vh - 64px)'
                  }}>
                    <FeedbackManagementPage />
                  </Content>
                </ProtectedRoute>
              } />
              <Route path="/feedback" element={
                <Content style={{
                  padding: '24px',
                  background: 'var(--rp-surface-page)',
                  minHeight: 'calc(100vh - 64px)'
                }}>
                  <MyFeedbackPage />
                </Content>
              } />
            </>
          ) : (
            // Fallback - should not reach here if auth logic above is correct
            <Route path="*" element={<LoginPage />} />
          )}
        </Routes>
        {/* FeedbackModal - rendered outside Routes so it's available on all pages */}
        {isAuthenticated && user && (
          <FeedbackModal
            open={feedbackModalOpen}
            onClose={() => { setFeedbackModalOpen(false); setErrorContext(undefined); }}
            errorContext={errorContext}
          />
        )}
        {/* EulaModal - blocking modal for EULA acceptance (clickwrap agreement) */}
        {isAuthenticated && user && (
          <EulaModal
            open={needsEulaAcceptance(user.eulaAcceptedVersion)}
            previouslyAcceptedVersion={user.eulaAcceptedVersion}
            onAccept={() => {
              // Refresh the page to reload user with updated EULA version from token
              window.location.reload()
            }}
          />
        )}
        {/* Token Info Modal - Dev tool to display decoded token/user info */}
        <Modal
          title={<><IdcardOutlined /> Token Info</>}
          open={tokenInfoModalOpen}
          onCancel={() => setTokenInfoModalOpen(false)}
          footer={null}
          width={600}
        >
          {user ? (
            <>
              <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="User ID">
                  <Typography.Text copyable code>{user.id}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  <Typography.Text copyable>{user.email}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="Display Name">{user.name}</Descriptions.Item>
                <Descriptions.Item label="Current Org">
                  {user.currentOrganization ? (
                    <>
                      <Typography.Text copyable code>{user.currentOrganization.id}</Typography.Text>
                      <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                        ({user.currentOrganization.name})
                      </Typography.Text>
                    </>
                  ) : (
                    <Typography.Text type="secondary">None</Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Org Role">
                  {user.currentOrganization?.role && (
                    <Tag color={user.currentOrganization.role === 'admin' ? 'gold' : 'blue'}>
                      {user.currentOrganization.role}
                    </Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Global Roles">
                  {user.globalRoles && user.globalRoles.length > 0 ? (
                    user.globalRoles.map(role => (
                      <Tag key={role} color="purple">{role}</Tag>
                    ))
                  ) : (
                    <Typography.Text type="secondary">None</Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Plan">
                  {user.plan ? (() => {
                    // Parse plan if it's a JSON string
                    const planData = typeof user.plan === 'string'
                      ? (() => { try { return JSON.parse(user.plan as string) } catch { return null } })()
                      : user.plan
                    return planData ? (
                      <>
                        <Tag color="green">{planData.tier}</Tag>
                        {planData.status && (
                          <Tag color={planData.status === 'active' ? 'blue' : 'orange'}>
                            {planData.status}
                          </Tag>
                        )}
                        {planData.sku_limit && (
                          <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                            ({planData.sku_limit.toLocaleString()} SKUs)
                          </Typography.Text>
                        )}
                      </>
                    ) : (
                      <Typography.Text code>{String(user.plan).substring(0, 50)}...</Typography.Text>
                    )
                  })() : (
                    <Typography.Text type="secondary">None</Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Effective Feature Tier">
                  {(() => {
                    const effectiveTier = featureTierService.getEffectiveTier(user)
                    const tierColors: Record<string, string> = {
                      'Dev': 'red',
                      'Experimental': 'orange',
                      'Early Access': 'blue',
                      'GA': 'default'
                    }
                    return (
                      <>
                        <Tag color={tierColors[effectiveTier] || 'default'}>{effectiveTier}</Tag>
                        {featureTierService.hasInternalAccess(user) && (
                          <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                            (via global role)
                          </Typography.Text>
                        )}
                      </>
                    )
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="EULA Accepted">
                  {user.eulaAcceptedVersion !== null && user.eulaAcceptedVersion !== undefined ? (
                    <Tag color="green">v{user.eulaAcceptedVersion}</Tag>
                  ) : (
                    <Tag color="orange">Not accepted</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Token Expiry">
                  {user.tokenExpiry ? (
                    <>
                      <Typography.Text>
                        {new Date(user.tokenExpiry * 1000).toLocaleString()}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                        ({Math.round((user.tokenExpiry * 1000 - Date.now()) / 60000)} min remaining)
                      </Typography.Text>
                    </>
                  ) : (
                    <Typography.Text type="secondary">Unknown</Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Organizations">
                  {user.organizations?.map(org => (
                    <Tag key={org.id} color={org.id === user.currentOrganization?.id ? 'blue' : 'default'}>
                      {org.name} ({org.role})
                    </Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Note: JWT tokens are stored in httpOnly cookies and cannot be decoded client-side.
                This displays the user info returned by the /me endpoint.
              </Typography.Text>
            </>
          ) : (
            <Typography.Text type="secondary">No user data available</Typography.Text>
          )}
        </Modal>
        {/* MetadataInspector - Dev tool to browse entity metadata */}
        <MetadataInspector
          open={metadataInspectorOpen}
          onClose={() => setMetadataInspectorOpen(false)}
        />
      </Layout>
        </TimezoneProvider>
      </AntApp>
    </ConfigProvider>
  )
}

export default App